import { Injectable } from '@angular/core';
import { GetProductContractsReq, IProduct, ProductResp, SHIPMENT_LOCATION_TYPE_ID } from '@cemex-core/cmx-api';
import { CmxHttpClient } from '@cemex/cmx-client-layout';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ContractLoadCompleteAction } from '@ordersnproducts/modules/order-builder/modules/specification/actions/contract.actions';
import { SALES_DOCUMENT_TYPE_ID } from '@ordersnproducts/modules/shared/constants/sales-document-type-id';
import { Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
  OneClickContractActionTypes,
  OneClickLoadContractsAction,
  OneClickLoadContractsCompleteAction,
} from '../actions/one-click-contract.actions';
import { OneClickOrdersActionTypes, OneClickSelectOrderAction } from '../actions/one-click-orders.actions';
import { State } from '../reducers';
import { getOneClickSelectedOrder } from './../selectors/one-click-orders.selector';

@Injectable()
export class OneClickContractEffects {
  loadStandardLotCommand = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OneClickOrdersActionTypes.SELECT_ORDER),
        tap((action: OneClickSelectOrderAction) => {
          action.payload.items.forEach((item, index) => {
            const product = item.product;
            this.store.dispatch(
              new OneClickLoadContractsAction(
                {
                  productId: product.productId,
                  commercialDesc: product.commercialDesc,
                } as IProduct,
                index,
              ),
            );
          });
        }),
      ),
    { dispatch: false },
  );

  loadContract = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickContractActionTypes.LOAD_CONTRACTS),
      switchMap((action: OneClickLoadContractsAction) => {
        return this.store.select(getOneClickSelectedOrder).pipe(
          take(1),
          map((data) => {
            return {
              data: {
                productId: action.payload.productId,
                commercialDesc: action.payload.commercialDesc,
                shipmentLocationId: data.jobsite.jobsiteId,
                shipmentLocationPodId: data.pointOfDelivery && data.pointOfDelivery.pointOfDeliveryId,
                shippingConditionId: String(data.shippingCondition.shippingConditionId),
              },
              index: action.index,
            };
          }),
        );
      }),
      switchMap(({ data, index }) => {
        // Add point of delivery to parameter if any
        let shipmentLocationId = `${data.shipmentLocationId}.${SHIPMENT_LOCATION_TYPE_ID.JOBSITE}`;
        if (data.shipmentLocationPodId) {
          shipmentLocationId = `${shipmentLocationId},${data.shipmentLocationPodId}.${SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY}`;
        }
        return this.getProductContracts({
          productId: String(data.productId),
          salesDocumentTypeId: String(SALES_DOCUMENT_TYPE_ID.CONTRACT),
          shipmentLocationId: `${shipmentLocationId}`,
          shippingConditionId: data.shippingConditionId,
        }).pipe(
          map((response: ProductResp) => {
            // Filter out products that has different name
            const salesDocuments = response.products
              .filter((product) => {
                return data.productId === product.product.productId;
              })
              .map((product) => {
                return product.salesDocument;
              });

            this.store.dispatch(
              new ContractLoadCompleteAction({ productId: data.productId, salesDocuments: salesDocuments }),
            );

            return new OneClickLoadContractsCompleteAction(salesDocuments, index);
          }),
        );
      }),
    ),
  );

  /**
   * Temp fix for api service
   * Cemex service is transforming the strings given into ASCII values
   * So for example: '715823.2,632277.3' is becoming '715823.2%2C632277.3'
   * @param request
   */
  private getProductContracts(request: GetProductContractsReq): Observable<ProductResp> {
    return this.http.get<ProductResp>(
      `/v2/mm/myproducts?productId=${request.productId}&salesDocumentTypeId=${request.salesDocumentTypeId}` +
        `&shippingConditionId=${request.shippingConditionId}&shipmentLocationId=${request.shipmentLocationId}`,
    );
  }

  constructor(
    private store: Store<State>,
    private actions$: Actions,
    private http: CmxHttpClient,
  ) {}
}
